import { useState, type CSSProperties } from "react";

import type { ChallengeCard } from "@/pages/play/catalog";
import {
  playTimePresets,
  type ChallengeVariant,
} from "@/pages/play/catalog";
import {
  challengeDifficultyModes,
  resolveDifficultyLabel,
} from "@/pages/play/challengeDifficulty";
import { KnightBadge } from "@/pages/play/KnightBadge";
import type { PlayEngineVersion } from "@/pages/play/engineVersion";
import { OpeningSeedDialog } from "@/pages/play/openingSeed/OpeningSeedDialog";
import type { OpeningSeedSelection } from "@/pages/play/openingSeed/openingSeedTypes";
import { useI18n } from "@/shared/i18n/LanguageContext";

type ChallengeSetupModalProps = {
  card: ChallengeCard;
  selectedVariantId: string | null;
  onSelectVariant: (variantId: string) => void;
  side: "white" | "black";
  onSelectSide: (side: "white" | "black") => void;
  hardness: number;
  engineVersion: PlayEngineVersion;
  onHardnessChange: (value: number) => void;
  minutes: number;
  incrementSeconds: number;
  onMinutesChange: (value: number) => void;
  onIncrementChange: (value: number) => void;
  onClose: () => void;
  onStart: () => void;
  isStarting: boolean;
  error: string | null;
  openingSeed: OpeningSeedSelection | null;
  onOpeningSeedChange: (seed: OpeningSeedSelection | null) => void;
};

function resolveAccentColor(accent: ChallengeCard["accent"]) {
  switch (accent) {
    case "steel":
      return "var(--steel)";
    case "acid":
      return "var(--acid)";
    case "blood":
      return "var(--blood)";
    default:
      return "var(--ember)";
  }
}

function openingSeedSummary(
  seed: OpeningSeedSelection | null,
  labels: {
    standardStart: string;
    white: string;
    black: string;
    toMove: string;
  },
) {
  if (!seed || seed.moves.length === 0) {
    return labels.standardStart;
  }
  const side = seed.sideToMove === "white" ? labels.white : labels.black;
  return `${seed.moves.length} ply - ${labels.toMove.replace("{side}", side)}`;
}

function resolveVariants(card: ChallengeCard): ChallengeVariant[] {
  if (card.variants?.length) {
    return card.variants;
  }
  if (!card.modelId) {
    return [];
  }
  return [
    {
      id: card.id,
      label: "Standard",
      modelId: card.modelId,
      elo: card.elo,
    },
  ];
}

export function ChallengeSetupModal({
  card,
  selectedVariantId,
  onSelectVariant,
  side,
  onSelectSide,
  hardness,
  engineVersion,
  onHardnessChange,
  minutes,
  incrementSeconds,
  onMinutesChange,
  onIncrementChange,
  onClose,
  onStart,
  isStarting,
  error,
  openingSeed,
  onOpeningSeedChange,
}: ChallengeSetupModalProps) {
  const { copy } = useI18n();
  const [openingDialogOpen, setOpeningDialogOpen] = useState(false);
  const variants = resolveVariants(card);
  const selectedVariant =
    variants.find((variant) => variant.id === selectedVariantId) ?? variants[0] ?? null;
  const summaryTime = `${minutes}+${incrementSeconds}`;
  const accentStyle = {
    "--challenge-accent": resolveAccentColor(card.accent),
  } as CSSProperties;
  const activeDifficultyId =
    resolveDifficultyLabel(hardness).toLowerCase() as keyof typeof copy.play.difficulty;
  const localizedDifficulty = copy.play.difficulty[activeDifficultyId];
  const engineLabel = copy.play.engineSwitch.versions[engineVersion].label;
  const positionSummary = openingSeedSummary(openingSeed, {
    standardStart: copy.play.opening.standardStart,
    white: copy.play.modal.white,
    black: copy.play.modal.black,
    toMove: copy.play.opening.toMove,
  });

  return (
    <div className="challenge-modal-backdrop" role="presentation" onClick={onClose}>
      <section
        className={`challenge-modal challenge-modal--${card.accent}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="challenge-modal-title"
        style={accentStyle}
        onClick={(event) => event.stopPropagation()}
      >
        <button className="challenge-modal__close" type="button" onClick={onClose}>
          {copy.play.modal.dismiss}
        </button>

        {variants.length > 1 ? (
          <div className="challenge-modal__cluster challenge-modal__version-strip">
            <div className="challenge-modal__section-head">
              <p className="challenge-modal__label">{copy.play.modal.versionLine}</p>
              <span className="challenge-modal__count">
                {variants.length} {copy.play.modal.models}
              </span>
            </div>
            <div className="challenge-modal__variant-grid">
              {variants.map((variant) => (
                <button
                  key={variant.id}
                  className={
                    selectedVariantId === variant.id
                      ? "challenge-variant challenge-variant--active"
                      : "challenge-variant"
                  }
                  type="button"
                  onClick={() => onSelectVariant(variant.id)}
                >
                  <strong>{variant.label}</strong>
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <div className="challenge-modal__frame">
          <aside className="challenge-modal__briefing">
            <p className="challenge-modal__eyebrow">{copy.play.modal.protocol}</p>

            <div className="challenge-modal__hero-panel">
              <p className="challenge-modal__type">{engineLabel}</p>
              <div className="challenge-modal__hero">
                <KnightBadge accent={card.accent} />
                <div>
                  <h2 id="challenge-modal-title">{card.title}</h2>
                </div>
              </div>
            </div>

            <div className="challenge-modal__summary">
              <div className="challenge-modal__summary-item">
                <span className="challenge-modal__summary-label">{copy.play.modal.engine}</span>
                <strong className="challenge-modal__summary-value">{engineLabel}</strong>
              </div>
              <div className="challenge-modal__summary-item">
                <span className="challenge-modal__summary-label">{copy.play.modal.line}</span>
                <strong className="challenge-modal__summary-value">
                  {selectedVariant?.label ?? copy.play.standard}
                </strong>
              </div>
              <div className="challenge-modal__summary-item">
                <span className="challenge-modal__summary-label">{copy.play.modal.seat}</span>
                <strong className="challenge-modal__summary-value">
                  {side === "white" ? copy.play.modal.white : copy.play.modal.black}
                </strong>
              </div>
              <div className="challenge-modal__summary-item">
                <span className="challenge-modal__summary-label">{copy.play.modal.difficulty}</span>
                <strong className="challenge-modal__summary-value">
                  {localizedDifficulty}
                </strong>
              </div>
              <div className="challenge-modal__summary-item">
                <span className="challenge-modal__summary-label">{copy.play.modal.clock}</span>
                <strong className="challenge-modal__summary-value">{summaryTime}</strong>
              </div>
            </div>

            <div className="challenge-opening-row">
              <div>
                <span>{copy.play.opening.entryLabel}</span>
                <strong>{positionSummary}</strong>
              </div>
              <div className="challenge-opening-row__actions">
                {openingSeed ? (
                  <button
                    type="button"
                    onClick={() => onOpeningSeedChange(null)}
                  >
                    {copy.play.opening.clear}
                  </button>
                ) : null}
                <button type="button" onClick={() => setOpeningDialogOpen(true)}>
                  {openingSeed ? copy.play.opening.edit : copy.play.opening.set}
                </button>
              </div>
            </div>

          </aside>

          <div className="challenge-modal__form">
            <div className="challenge-modal__cluster">
              <div className="challenge-modal__section-head">
                <p className="challenge-modal__label">{copy.play.modal.seatSelection}</p>
              </div>
              <div className="challenge-side-row">
                <button
                  className={
                    side === "white"
                      ? "challenge-side challenge-side--active"
                      : "challenge-side"
                  }
                  type="button"
                  onClick={() => onSelectSide("white")}
                >
                  <span className="challenge-side__token challenge-side__token--white">♔</span>
                  <span className="challenge-side__copy">
                    <strong>{copy.play.modal.white}</strong>
                  </span>
                </button>

                <button
                  className={
                    side === "black"
                      ? "challenge-side challenge-side--active"
                      : "challenge-side"
                  }
                  type="button"
                  onClick={() => onSelectSide("black")}
                >
                  <span className="challenge-side__token challenge-side__token--black">♚</span>
                  <span className="challenge-side__copy">
                    <strong>{copy.play.modal.black}</strong>
                  </span>
                </button>
              </div>
            </div>

            <div className="challenge-modal__cluster">
              <div className="challenge-modal__section-head">
                <p className="challenge-modal__label">{copy.play.modal.difficulty}</p>
              </div>
              <div className="challenge-mode-grid">
                {challengeDifficultyModes.map((mode) => (
                  <button
                    key={mode.id}
                    className={
                      activeDifficultyId === mode.id
                        ? "challenge-mode challenge-mode--active"
                        : "challenge-mode"
                    }
                    type="button"
                    onClick={() => onHardnessChange(mode.hardness)}
                  >
                    <strong>{copy.play.difficulty[mode.id]}</strong>
                  </button>
                ))}
              </div>
              <label className="challenge-slider challenge-slider--wide">
                <span>{copy.play.modal.hardness}</span>
                <strong>{hardness}/10</strong>
                <input
                  type="range"
                  min="1"
                  max="10"
                  step="1"
                  value={hardness}
                  onChange={(event) => onHardnessChange(Number(event.target.value))}
                />
              </label>
            </div>

            <div className="challenge-modal__cluster">
              <div className="challenge-modal__section-head">
                <p className="challenge-modal__label">{copy.play.modal.timeControl}</p>
              </div>
              <div className="challenge-preset-grid">
                {playTimePresets.map((preset) => (
                  <button
                    key={preset.label}
                    className={
                      minutes === preset.minutes &&
                      incrementSeconds === preset.incrementSeconds
                        ? "challenge-preset challenge-preset--active"
                        : "challenge-preset"
                    }
                    type="button"
                    onClick={() => {
                      onMinutesChange(preset.minutes);
                      onIncrementChange(preset.incrementSeconds);
                    }}
                  >
                    <strong>{preset.label}</strong>
                  </button>
                ))}
              </div>

              <div className="challenge-slider-grid">
                <label className="challenge-slider">
                  <span>{copy.play.modal.time}</span>
                  <strong>{minutes} {copy.play.modal.min}</strong>
                  <input
                    type="range"
                    min="1"
                    max="30"
                    step="1"
                    value={minutes}
                    onChange={(event) => onMinutesChange(Number(event.target.value))}
                  />
                </label>

                <label className="challenge-slider">
                  <span>{copy.play.modal.increment}</span>
                  <strong>{incrementSeconds} {copy.play.modal.sec}</strong>
                  <input
                    type="range"
                    min="0"
                    max="15"
                    step="1"
                    value={incrementSeconds}
                    onChange={(event) => onIncrementChange(Number(event.target.value))}
                  />
                </label>
              </div>
            </div>

            {error ? <p className="challenge-modal__error">{error}</p> : null}

            <div className="challenge-modal__actions">
              <button
                className="button-link button-link--secondary"
                type="button"
                onClick={onClose}
              >
                {copy.play.modal.cancel}
              </button>
              <button
                className="button-link button-link--primary"
                type="button"
                onClick={onStart}
                disabled={isStarting}
              >
                {isStarting ? copy.play.modal.starting : copy.play.modal.launch}
              </button>
            </div>
          </div>
        </div>

        {openingDialogOpen ? (
          <OpeningSeedDialog
            initialSeed={openingSeed}
            onConfirm={(seed) => {
              onOpeningSeedChange(seed);
              setOpeningDialogOpen(false);
            }}
            onClose={() => setOpeningDialogOpen(false)}
          />
        ) : null}
      </section>
    </div>
  );
}
